import React, { useRef, useEffect } from 'react';
import { Canvas } from '@react-three/fiber';
import styled from 'styled-components';
import { useCanvasContext } from '../../contexts/CanvasContext';
import { useGPUTier } from '../../hooks/useGPUTier';
import { usePreloading } from '../../contexts/SiteContext';
import { clearResponsiveCaches } from '../../config/sceneConfig';
import { logger } from '../../utils/logger';
import { setCanvasPointer, setCanvasElementRef } from '../../utils/interactive3DHelpers';

const CanvasContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: ${props => props.$debugPreload ? 9998 : 0};
  pointer-events: auto;
  
  /* Reset cursor when pointer leaves canvas area */
  &:hover {
    /* Cursor will be set by individual 3D elements via onPointerOver */
  }
`;

const PersistentCanvas = ({ children }) => {
  const canvasRef = useRef();
  const containerRef = useRef();
  const { cameraRef, canvasElementRef } = useCanvasContext();
  const { performanceSettings } = useGPUTier();
  const { isPreloading } = usePreloading();
  const debugPreloadRef = useRef(false);
  
  // Check for debug flag in URL (dev mode only)
  useEffect(() => {
    // Only enable debug mode in development
    const isDevelopment = process.env.NODE_ENV === 'development';
    if (!isDevelopment) {
      return;
    }
    
    const urlParams = new URLSearchParams(window.location.search);
    const debugPreloadFlag = urlParams.get('debugPreload') === 'true';
    debugPreloadRef.current = debugPreloadFlag;
    if (debugPreloadFlag) {
      logger.debug('🔍 Debug mode: Canvas will be visible during preload');
    }
  }, []);

  // Handle window resize to update camera and clear caches
  useEffect(() => {
    const handleResize = () => {
      // Clear responsive caches so positions recalculate with new screen size
      clearResponsiveCaches();
      
      // Update camera aspect ratio if camera exists
      if (cameraRef?.current) {
        const width = window.innerWidth;
        const height = window.innerHeight;
        const aspect = width / height;
        
        if (cameraRef.current.aspect !== aspect) {
          cameraRef.current.aspect = aspect;
          cameraRef.current.updateProjectionMatrix();
          logger.scene('Camera aspect ratio updated on resize:', aspect);
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [cameraRef]);

  // Reset cursor when mouse leaves canvas area
  useEffect(() => {
    const handleMouseLeave = () => {
      setCanvasPointer(false);
    };
    
    const handleWindowMouseLeave = () => {
      // Fallback: reset cursor when mouse leaves the window
      setCanvasPointer(false);
    };
    
    const container = containerRef.current;
    if (container) {
      container.addEventListener('mouseleave', handleMouseLeave);
    }
    
    // Also listen on window as fallback
    window.addEventListener('mouseout', handleWindowMouseLeave);
    
    return () => {
      if (container) {
        container.removeEventListener('mouseleave', handleMouseLeave);
      }
      window.removeEventListener('mouseout', handleWindowMouseLeave);
    };
  }, []);

  return (
    <CanvasContainer ref={containerRef} data-canvas-container $debugPreload={debugPreloadRef.current && isPreloading}>
      <Canvas
        ref={canvasRef}
        camera={{ 
          position: [0, 0, 15], 
          fov: 60,
          near: 0.1,
          far: 1000
        }}
        onCreated={({ camera, gl }) => {
          // Store the camera reference in the context
          if (cameraRef) {
            cameraRef.current = camera;
            logger.scene('Camera registered');
          }
          // Store reference to the canvas DOM element in context
          if (gl && gl.domElement && canvasElementRef) {
            canvasElementRef.current = gl.domElement;
            // Register the ref in the helper function
            setCanvasElementRef(canvasElementRef);
            // Set up mouse leave handler on canvas element
            const handleCanvasMouseLeave = () => {
              setCanvasPointer(false);
            };
            gl.domElement.addEventListener('mouseleave', handleCanvasMouseLeave);
          }
        }}
        style={{ 
          background: 'transparent',
          width: '100%',
          height: '100%'
        }}
        gl={{ 
          alpha: true, 
          antialias: performanceSettings.enableAntialias,
          powerPreference: "high-performance"
        }}
        shadows={performanceSettings.enableShadows}
        dpr={performanceSettings.dpr}
        onPointerMissed={(event) => {
          // Reset cursor when clicking in empty space
          setCanvasPointer(false);
          // Debug: Log when clicking in empty space
          logger.debug('=== EMPTY SPACE CLICK DEBUG ===');
          logger.debug('Mouse position:', { x: event.clientX, y: event.clientY });
          logger.debug('========================');
        }}
      >
        {children}
      </Canvas>
    </CanvasContainer>
  );
};

export default PersistentCanvas;
